import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import Avatar from '../avatar/Avatar';
import Button from '../button/Button';
import IconButton from '../button/IconButton';
import ToolTip from '../tooltip/ToolTip';
import { DEFAULT_FPS, quantizeToFrame } from '../../utils/timecode';
import { formatFileSize } from '../file-browser/fileBrowserUtils';
import WaveformCanvas from './WaveformCanvas';
import { DRAG_EXPORT_MAX_BYTES, blobToDataUrl, buildClipFileName } from './core/audioClip';
import { clearDragClip, setDragClip } from './core/dragClipRegistry';
import { useAudioSource } from './core/useAudioSource';
import { useAudioTransport } from './core/useAudioTransport';
import { RangeSelection, useRangeSelection } from './core/useRangeSelection';

/**
 * サウンドライブラリ用のオーディオインスペクタバー（Soundly / Nuendo 風）。
 *
 * ファイル一覧で選択中のオーディオを画面下に固定表示し、波形上のドラッグで
 * 範囲を決めて、その範囲だけを Finder / DAW へドラッグ書き出しできる。
 *
 * - 波形: 原本から算出した高密度 peaks（届くまでは Lambda の 20 点/秒を先出し）
 * - 再生: 変換済み m4a（軽い）。範囲があれば out 点で in 点へ戻すループ再生
 * - 書き出し: WAV 原本はバイトコピーで無変換、それ以外は WAV 24bit へ再エンコード
 * - ドラッグ書き出しは Chromium の DownloadURL に依存する。非対応ブラウザと
 *   上限超過の範囲では、同じ内容をダウンロードするボタンに切り替える
 */

export interface AudioInspectorFile {
  id: string;
  name: string;
  /** 原本 URL。高密度波形と範囲書き出しの取得元 */
  originalUrl: string;
  /** 再生用の変換済み URL。無ければ原本を再生する */
  optimizedUrl?: string | null;
  /** Lambda 生成 peaks JSON の URL（原本が届くまでの先出し表示） */
  waveformUrl?: string | null;
}

/** 波形上に重ねるチーム共有のブックマーク（点 or 範囲） */
export interface AudioInspectorBookmark {
  id: string;
  startSeconds: number;
  /** null = 点のブックマーク */
  endSeconds: number | null;
  label: string;
  /** 作成者名（アバター画像が無いピンのイニシャル表示に使う） */
  createdByName?: string | null;
  /** マーカー上端のピンに出す作成者アバター URL */
  createdByAvatar?: string | null;
}

/**
 * 外部（ブックマーク一覧ページ等）からのジャンプ指示。
 * requestId が変わるたびに 1 回だけ適用する。
 */
export interface AudioInspectorFocusRequest {
  requestId: number;
  startSeconds: number;
  /** null = 点（頭出しのみ）。値があれば範囲選択も復元する */
  endSeconds: number | null;
}

/**
 * 呼び出し側が既に再生エンジンを持っている場合に渡す操作口。
 * `useAudioTransport` / `useAudioAudition` の戻り値がそのまま当てはまる。
 */
export interface AudioInspectorTransport {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  toggle: () => void;
  pause: () => void;
  seekTo: (_seconds: number) => void;
  /** スペースキー再生のターゲットを自分にする（あれば開いた時に呼ぶ） */
  markActive?: () => void;
}

export interface AudioInspectorBarProps {
  /** 表示対象。null ならバーごと消える */
  file: AudioInspectorFile | null;
  /**
   * 外部の再生エンジン。一覧の試聴（useAudioAudition）と再生位置を共有したい
   * 場合に渡す。省略時はバーが自前の再生エンジンを持つ。
   */
  transport?: AudioInspectorTransport;
  /**
   * 上下キーでの前後ファイル移動。一覧側の選択を動かす想定で、
   * 未指定ならキー操作を受け付けない。
   */
  onSelectPrev?: () => void;
  onSelectNext?: () => void;
  /** 他の固定バー（BottomAppBar 等）と共存する場合の下端オフセット（px） */
  bottomOffset?: number;
  onClose?: () => void;
  /** 範囲の書き出し（ドラッグ / ダウンロード）が成立したとき */
  onExport?: (_fileId: string) => void;
  /**
   * 波形に重ねるブックマーク。API 呼び出しは持たず、表示と操作イベントだけを
   * 担う（presentational）。onCreateBookmark 未指定ならブックマーク UI ごと
   * 出さない。
   */
  bookmarks?: AudioInspectorBookmark[];
  /** 追加（選択範囲があれば範囲、無ければ現在位置の点）。B キーでも呼ばれる */
  onCreateBookmark?: (_params: {
    startSeconds: number;
    endSeconds: number | null;
  }) => Promise<void> | void;
  /** ラベルの変更 */
  onUpdateBookmark?: (
    // eslint-disable-next-line no-unused-vars
    bookmarkId: string,
    // eslint-disable-next-line no-unused-vars
    patch: { label: string },
  ) => Promise<void> | void;
  /** 削除 */
  // eslint-disable-next-line no-unused-vars
  onDeleteBookmark?: (_bookmarkId: string) => Promise<void> | void;
  /** 外部からの頭出し + 範囲復元の指示（ブックマーク一覧のジャンプ用） */
  focusRequest?: AudioInspectorFocusRequest | null;
  /** focusRequest を適用し終えたとき（呼び出し側が指示をクリアする） */
  onFocusRequestApplied?: () => void;
}

const WAVEFORM_HEIGHT = 56;
/** 上段（再生コントロール行）。中で一番背の高い IconButton に合わせる */
const CONTROL_ROW_HEIGHT = 36;
/** 最下段（時刻行）。選択の有無で中身が変わっても揺れないよう固定する */
const TIME_ROW_HEIGHT = 16;
const PANEL_PADDING_Y = 10;
const PANEL_BOTTOM_GAP = 16;

/**
 * バーが占める高さ（px）。バーは position: fixed で浮くため、
 * 一覧の末尾が隠れないよう呼び出し側がこのぶんの余白を確保する。
 *
 * 各行の高さを固定しているので、選択の有無に関わらずこの値と実寸が一致する。
 * 下端固定のバーで行の高さが変わると、波形ごと上下に揺れて見えてしまう。
 */
export const AUDIO_INSPECTOR_BAR_HEIGHT =
  PANEL_BOTTOM_GAP +
  2 /* border */ +
  PANEL_PADDING_Y * 2 +
  CONTROL_ROW_HEIGHT +
  WAVEFORM_HEIGHT +
  TIME_ROW_HEIGHT;

/**
 * OS へのドラッグ書き出しは Chromium 系の `DownloadURL` 独自プロトコルに依存する。
 * Safari / Firefox には代替が無いため、機能ごとダウンロードへ切り替える。
 */
const supportsOsDragExport = ((): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /Chrome|Chromium|Edg\//.test(ua) && !/Mobile|Android|CriOS|FxiOS/.test(ua);
})();

// ===== Styled =====

const BarContainer = styled.div<{ $bottomOffset: number }>`
  position: fixed;
  left: 16px;
  right: 16px;
  bottom: ${({ $bottomOffset }) =>
    `calc(${PANEL_BOTTOM_GAP + $bottomOffset}px + env(safe-area-inset-bottom))`};
  z-index: 900; /* Modal(1000) 未満、ページ内固定バー(4)より上 */
  display: flex;
  justify-content: center;
  pointer-events: none;
`;

/** AudioPlayerBar / ActionPanel と同じ半透明グラスの浮遊パネル */
const BarPanel = styled.div`
  pointer-events: auto;
  width: 100%;
  max-width: 1080px;
  border-radius: 12px;
  border: 1px solid #f0f0f0;
  background: rgba(255, 255, 255, 0.16);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(13px);
  -webkit-backdrop-filter: blur(13px);
  padding: ${PANEL_PADDING_Y}px 16px;
`;

/** 中身の出入りでバー全体が上下しないよう、上下段とも高さを固定する */
const ControlRow = styled.div.attrs({ className: 'u-row u-gap16' })`
  height: ${CONTROL_ROW_HEIGHT}px;
`;

const TimeRow = styled.div.attrs({
  className: 'u-row u-gap8 bodytext-11 u-text-light-gray u-line-height-100',
})`
  height: ${TIME_ROW_HEIGHT}px;
`;

/** ellipsis のために min-width: 0 が要る（utility に無い） */
const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

/** 波形と選択レイヤーの重ね置き場。ポインター操作はこの要素で受ける */
const WaveArea = styled.div`
  position: relative;
  width: 100%;
  height: ${WAVEFORM_HEIGHT}px;
  cursor: crosshair;
  touch-action: none;
  user-select: none;
`;

const SelectionRect = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  background: rgba(0, 154, 206, 0.16);
  border-left: 1px solid var(--soremo-blue);
  border-right: 1px solid var(--soremo-blue);
  pointer-events: none;
`;

/** in / out ハンドル。掴みやすいよう見た目より広い当たり判定を持たせる */
const SelectionHandle = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 12px;
  transform: translateX(-50%);
  cursor: ew-resize;
  touch-action: none;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 3px;
    transform: translateX(-50%);
    background: var(--soremo-blue);
    border-radius: 1.5px;
  }
`;

const Playhead = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: var(--soremo-gray);
  pointer-events: none;
`;

// ===== ブックマーク（チーム共有マーカー） =====
//
// ラインは選択範囲と同じ soremo-blue 系。塗り / 線は pointer-events: none に
// して波形上のドラッグ選択を妨げず、クリック・ホバーは上端の作成者アバター
// ピン（プレビューのタイムライン編集 CommentPinLayer と同じ流儀）で受ける。

const BOOKMARK_COLOR = 'var(--soremo-blue)';
/** タイムライン編集のコメントピン（20px）よりわずかに小さく、波形を隠さない */
const BOOKMARK_PIN_SIZE = 18;

/** 範囲ブックマークの塗り（操作は受けない）。選択範囲(0.16)より薄くする */
const BookmarkRangeFill = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  background: rgba(0, 154, 206, 0.1);
  border-left: 1px solid rgba(0, 154, 206, 0.55);
  border-right: 1px solid rgba(0, 154, 206, 0.35);
  pointer-events: none;
`;

/** 点ブックマークの縦線（操作は受けない） */
const BookmarkPointLine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: ${BOOKMARK_COLOR};
  opacity: 0.7;
  pointer-events: none;
`;

/**
 * 作成者アバターのピン。クリックでシーク + 範囲復元、ホバーで詳細を出す。
 * タイムライン編集の CommentPinLayer と同じくホバーでわずかに拡大する。
 * 波形を隠さないよう波形エリアの上端より上に浮かせ、下端だけラインに
 * 重ねる（バーのパネルは overflow: visible のためはみ出して描画される）。
 */
const BookmarkPin = styled.button`
  position: absolute;
  top: -${BOOKMARK_PIN_SIZE - 4}px;
  transform: translateX(-50%);
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  line-height: 0;
  z-index: 2;
  transition: scale 0.15s ease;

  &:hover,
  &:focus-visible {
    scale: 1.15;
    z-index: 6;
  }
`;

/** ホバーで出すラベル・作成者・編集/削除のポップアップ */
const BookmarkPopup = styled.div`
  position: absolute;
  bottom: calc(100% + 4px);
  transform: translateX(-50%);
  z-index: 5;
  width: 220px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.14);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  cursor: default;
`;

/** ポップアップ内のラベル編集（FileBrowser の RenameInput と同じ最小構成） */
const BookmarkLabelInput = styled.input.attrs({ type: 'text' })`
  width: 100%;
`;

/**
 * ドラッグして OS / DAW へ渡すチップ。
 * Button はドラッグ元にできない（draggable な要素はクリック挙動と競合する）ため、
 * ここだけは専用の見た目を持たせる。
 */
const DragChip = styled.div.attrs({ className: 'u-row-center u-gap4' })<{ $enabled: boolean }>`
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid ${({ $enabled }) => ($enabled ? 'var(--soremo-blue)' : 'var(--soremo-border)')};
  color: ${({ $enabled }) => ($enabled ? 'var(--soremo-blue)' : 'var(--soremo-light-gray)')};
  background: rgba(255, 255, 255, 0.6);
  cursor: ${({ $enabled }) => ($enabled ? 'grab' : 'default')};
  white-space: nowrap;

  &:active {
    cursor: ${({ $enabled }) => ($enabled ? 'grabbing' : 'default')};
  }

  /* アイコン用の 16px は u-fontsize-* に定義が無いためここで指定する */
  & > .material-symbols-rounded {
    font-size: 16px;
  }
`;

// ===== ヘルパー =====

/**
 * 秒数を 0:00.00（1/100 秒精度）で表示する。
 * バー下段の時刻表示のほか、ブックマーク一覧の位置表示でも使う
 * （timecode.ts の M:SS.FF はフレーム番号表記で意味が異なる）。
 */
export function formatPreciseTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00.00';
  const minutes = Math.floor(seconds / 60);
  const rest = seconds - minutes * 60;
  return `${minutes}:${rest.toFixed(2).padStart(5, '0')}`;
}

/** 文字入力中はキーボードショートカットを奪わない */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return !!target.closest('input, textarea, select, [contenteditable="true"]');
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  // revoke が早すぎるとダウンロードが中断されるため 1 フレームでは足りない
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * ドラッグ書き出しに備えて先に用意しておく切り出し済みクリップ。
 * 上限超過 / 非対応ブラウザでは用意せず、ダウンロードボタンへ回す。
 */
interface PreparedClip {
  blob: Blob;
  fileName: string;
  /** DownloadURL に渡す data URL */
  dataUrl: string;
}

// ===== 本体 =====

const AudioInspectorBar: React.FC<AudioInspectorBarProps> = ({
  file,
  transport: externalTransport,
  onSelectPrev,
  onSelectNext,
  bottomOffset = 0,
  onClose,
  onExport,
  bookmarks = [],
  onCreateBookmark,
  onUpdateBookmark,
  onDeleteBookmark,
  focusRequest = null,
  onFocusRequestApplied,
}) => {
  const [committed, setCommitted] = useState<RangeSelection | null>(null);
  const [clip, setClip] = useState<PreparedClip | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [loopEnabled, setLoopEnabled] = useState(true);
  const [creatingBookmark, setCreatingBookmark] = useState(false);
  // ホバー中のブックマーク（ポップアップ表示対象）。編集中は閉じない
  const [hoveredBookmarkId, setHoveredBookmarkId] = useState<string | null>(
    null,
  );
  const [editingBookmark, setEditingBookmark] = useState<{
    id: string;
    value: string;
  } | null>(null);
  // フラグ → ポップアップへマウス移動する間に閉じないための猶予タイマー
  const popupCloseTimerRef = useRef<number | null>(null);

  const source = useAudioSource({
    src: file?.originalUrl ?? null,
    fallbackPeaksUrl: file?.waveformUrl ?? null,
  });

  // 外部エンジンを受け取っている間は自前の要素を作らない（同時再生を避ける）。
  // 自前で鳴らす場合は軽い変換済み音源を優先する
  const ownSrc =
    externalTransport || !file ? null : file.optimizedUrl || file.originalUrl;
  const ownTransport = useAudioTransport({
    src: ownSrc,
    initialDuration: source.duration,
  });
  const transport = externalTransport ?? ownTransport;

  // 波形の横軸は原本の尺を正とする。m4a は AAC のエンコーダ遅延ぶん尺が伸びるため、
  // 原本が読めているあいだはそちらに合わせないと選択位置と書き出し位置がずれる。
  const timelineDuration = source.duration > 0 ? source.duration : transport.duration;

  const seekTo = transport.seekTo;
  const handleCommit = useCallback((next: RangeSelection | null) => {
    setCommitted(next);
  }, []);

  const selection = useRangeSelection({
    duration: timelineDuration,
    onSeek: seekTo,
    onCommit: handleCommit,
  });

  const { clear: clearSelection } = selection;

  const fileId = file?.id ?? null;

  // ファイルが変わったら選択と切り出し済みクリップを捨てる
  useEffect(() => {
    clearSelection();
    setClip(null);
    setExportError(null);
    setHoveredBookmarkId(null);
    setEditingBookmark(null);
  }, [fileId, clearSelection]);

  // 開いているあいだはスペースキー再生の対象をこの音源にする
  // （波形やバーを触ってフォーカスが一覧から外れてもスペースで再生できる）
  const markActiveRef = useRef(transport.markActive);
  markActiveRef.current = transport.markActive;
  useEffect(() => {
    if (fileId) markActiveRef.current?.();
  }, [fileId]);

  // 上下キーで前後のオーディオへ移動する。
  // 一覧の行にフォーカスがある間は行側のハンドラが処理済み（preventDefault 済み）
  // なので、そちらを優先して二重移動を避ける。
  const onSelectPrevRef = useRef(onSelectPrev);
  onSelectPrevRef.current = onSelectPrev;
  const onSelectNextRef = useRef(onSelectNext);
  onSelectNextRef.current = onSelectNext;
  useEffect(() => {
    if (!fileId) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
      if (event.defaultPrevented || event.isComposing) return;
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      if (isTypingTarget(event.target)) return;
      const move =
        event.key === 'ArrowUp' ? onSelectPrevRef.current : onSelectNextRef.current;
      if (!move) return;
      event.preventDefault();
      move();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fileId]);

  // プレビューページと同じ再生ショートカット:
  //   ←→ 1 フレーム移動 / SHIFT+←→ 1 秒移動（再生中は一時停止してステップ）
  //   K 再生・停止 / J 5 秒戻る / L 5 秒進む
  // オーディオはプレビューと同じく DEFAULT_FPS(30) でフレーム換算する。
  const transportRef = useRef(transport);
  transportRef.current = transport;
  const timelineDurationRef = useRef(timelineDuration);
  timelineDurationRef.current = timelineDuration;
  useEffect(() => {
    if (!fileId) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.isComposing) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;
      const t = transportRef.current;
      const duration = timelineDurationRef.current;
      const clamp = (seconds: number) =>
        Math.max(0, duration > 0 ? Math.min(duration, seconds) : seconds);
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const delta = event.shiftKey ? direction : direction / DEFAULT_FPS;
        t.pause();
        t.seekTo(
          quantizeToFrame(clamp(t.currentTime + delta), DEFAULT_FPS),
        );
        return;
      }
      if (event.shiftKey) return;
      if (event.key === 'k' || event.key === 'K') {
        event.preventDefault();
        t.toggle();
      } else if (event.key === 'j' || event.key === 'J') {
        event.preventDefault();
        t.seekTo(clamp(t.currentTime - 5));
      } else if (event.key === 'l' || event.key === 'L') {
        event.preventDefault();
        t.seekTo(clamp(t.currentTime + 5));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fileId]);

  // 範囲再生: out 点に達したら in 点へ戻す（ループ OFF なら out 点で止める）
  useEffect(() => {
    if (!committed || !transport.isPlaying) return;
    if (transport.currentTime < committed.end) return;
    if (loopEnabled) {
      transport.seekTo(committed.start);
    } else {
      transport.pause();
      transport.seekTo(committed.end);
    }
  }, [committed, loopEnabled, transport]);

  // ===== ブックマーク =====

  // 追加: 範囲が選択されていれば範囲、無ければ現在位置の点を作る
  const handleAddBookmark = useCallback(() => {
    if (!onCreateBookmark || creatingBookmark) return;
    const params = committed
      ? { startSeconds: committed.start, endSeconds: committed.end }
      : { startSeconds: transport.currentTime, endSeconds: null };
    setCreatingBookmark(true);
    void Promise.resolve(onCreateBookmark(params)).finally(() => {
      setCreatingBookmark(false);
    });
  }, [onCreateBookmark, creatingBookmark, committed, transport]);

  // B キーで追加（Space / ↑↓ と同じ流儀。文字入力中は奪わない）
  const handleAddBookmarkRef = useRef(handleAddBookmark);
  handleAddBookmarkRef.current = handleAddBookmark;
  const hasCreateBookmark = !!onCreateBookmark;
  useEffect(() => {
    if (!fileId || !hasCreateBookmark) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'b' && event.key !== 'B') return;
      if (event.defaultPrevented || event.isComposing) return;
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      if (isTypingTarget(event.target)) return;
      event.preventDefault();
      handleAddBookmarkRef.current();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fileId, hasCreateBookmark]);

  // マーカークリック: 頭出し + 範囲ブックマークなら選択範囲を復元する
  // （復元した範囲はそのまま WAV 切り出し・ドラッグ書き出しに使える）
  const setSelectionRange = selection.set;
  const handleBookmarkJump = useCallback(
    (bookmark: AudioInspectorBookmark) => {
      seekTo(bookmark.startSeconds);
      if (bookmark.endSeconds != null) {
        setSelectionRange({
          start: bookmark.startSeconds,
          end: bookmark.endSeconds,
        });
      }
    },
    [seekTo, setSelectionRange],
  );

  // 外部（ブックマーク一覧ページ）からのジャンプ指示。requestId ごとに
  // 1 回だけ適用する。シークはメタデータ未取得でも pendingSeek で反映される。
  // 適用後は onFocusRequestApplied で呼び出し側に指示をクリアしてもらう
  // （残ったままだと、後で同じファイルを開き直したときに再適用されてしまう）。
  const appliedFocusRequestIdRef = useRef(0);
  const onFocusRequestAppliedRef = useRef(onFocusRequestApplied);
  onFocusRequestAppliedRef.current = onFocusRequestApplied;
  useEffect(() => {
    if (!focusRequest) return;
    if (focusRequest.requestId === appliedFocusRequestIdRef.current) return;
    appliedFocusRequestIdRef.current = focusRequest.requestId;
    seekTo(focusRequest.startSeconds);
    if (focusRequest.endSeconds != null) {
      setSelectionRange({
        start: focusRequest.startSeconds,
        end: focusRequest.endSeconds,
      });
    }
    onFocusRequestAppliedRef.current?.();
  }, [focusRequest, seekTo, setSelectionRange]);

  const cancelPopupClose = useCallback(() => {
    if (popupCloseTimerRef.current != null) {
      window.clearTimeout(popupCloseTimerRef.current);
      popupCloseTimerRef.current = null;
    }
  }, []);

  const handleMarkerEnter = useCallback(
    (bookmarkId: string) => {
      cancelPopupClose();
      setHoveredBookmarkId(bookmarkId);
    },
    [cancelPopupClose],
  );

  const handleMarkerLeave = useCallback(() => {
    cancelPopupClose();
    popupCloseTimerRef.current = window.setTimeout(() => {
      setHoveredBookmarkId(null);
    }, 150);
  }, [cancelPopupClose]);

  useEffect(() => cancelPopupClose, [cancelPopupClose]);

  const commitBookmarkLabel = useCallback(() => {
    if (!editingBookmark) return;
    const { id, value } = editingBookmark;
    setEditingBookmark(null);
    if (onUpdateBookmark) {
      void onUpdateBookmark(id, { label: value.trim() });
    }
  }, [editingBookmark, onUpdateBookmark]);

  const handleDeleteBookmark = useCallback(
    (bookmarkId: string) => {
      if (!onDeleteBookmark) return;
      setHoveredBookmarkId(null);
      setEditingBookmark(null);
      void onDeleteBookmark(bookmarkId);
    },
    [onDeleteBookmark],
  );

  const estimatedBytes = useMemo(
    () => (committed ? source.estimateBytes(committed.start, committed.end) : 0),
    [committed, source],
  );
  const withinDragLimit = estimatedBytes > 0 && estimatedBytes <= DRAG_EXPORT_MAX_BYTES;

  // dragstart は同期でしか DataTransfer を触れないため、選択が確定した時点で
  // 切り出しと data URL 化を済ませておく。上限超過ぶんはボタン経由に回す。
  useEffect(() => {
    setClip(null);
    if (!file || !committed || !source.ready || !supportsOsDragExport || !withinDragLimit) {
      setPreparing(false);
      return;
    }
    let cancelled = false;
    setPreparing(true);
    void (async () => {
      try {
        const blob = await source.sliceToWav(committed.start, committed.end);
        if (cancelled || !blob) return;
        const dataUrl = await blobToDataUrl(blob);
        if (cancelled) return;
        setClip({
          blob,
          fileName: buildClipFileName(file.name, committed.start, committed.end),
          dataUrl,
        });
      } catch (error) {
        if (cancelled) return;
        console.warn('[AudioInspectorBar] 切り出しの準備に失敗:', error);
        setExportError('選択範囲を切り出せませんでした');
      } finally {
        if (!cancelled) setPreparing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [file, committed, source, withinDragLimit]);

  const onExportRef = useRef(onExport);
  onExportRef.current = onExport;

  const handleDragStart = useCallback(
    (event: React.DragEvent) => {
      if (!clip || !file) {
        event.preventDefault();
        return;
      }
      event.dataTransfer.effectAllowed = 'copy';
      // Chromium 独自。blob: URL を渡すと 0 バイトのファイルになるため data: を使う
      event.dataTransfer.setData('DownloadURL', `audio/wav:${clip.fileName}:${clip.dataUrl}`);
      // DownloadURL を伴うドラッグでは DataTransfer がアプリ内 drop まで届かないため、
      // 自アプリ向けの受け渡しはレジストリで行う
      setDragClip({
        blob: clip.blob,
        fileName: clip.fileName,
        mimeType: 'audio/wav',
        sourceFileId: file.id,
        startSeconds: committed?.start,
        endSeconds: committed?.end,
      });
    },
    [clip, file, committed],
  );

  const handleDragEnd = useCallback(
    (event: React.DragEvent) => {
      clearDragClip();
      // 途中でキャンセルされた場合は dropEffect が 'none' になる
      if (file && event.dataTransfer.dropEffect !== 'none') {
        onExportRef.current?.(file.id);
      }
    },
    [file],
  );

  const handleDownload = useCallback(async () => {
    if (!file || !committed) return;
    setExporting(true);
    setExportError(null);
    try {
      const blob = clip?.blob ?? (await source.sliceToWav(committed.start, committed.end));
      if (!blob) return;
      const fileName = clip?.fileName ?? buildClipFileName(file.name, committed.start, committed.end);
      downloadBlob(blob, fileName);
      onExportRef.current?.(file.id);
    } catch (error) {
      console.warn('[AudioInspectorBar] 範囲の書き出しに失敗:', error);
      setExportError('選択範囲を書き出せませんでした');
    } finally {
      setExporting(false);
    }
  }, [file, committed, clip, source]);

  if (!file) return null;

  const ratio = (seconds: number) =>
    timelineDuration > 0 ? Math.max(0, Math.min(1, seconds / timelineDuration)) * 100 : 0;
  const view = selection.selection;
  // ホバー / 編集中のブックマーク（ポップアップの表示対象）
  const activeBookmarkId = editingBookmark?.id ?? hoveredBookmarkId;
  const activeBookmark = activeBookmarkId
    ? (bookmarks.find((b) => b.id === activeBookmarkId) ?? null)
    : null;
  const canDrag = clip != null;
  const clipBytes = clip?.blob.size ?? estimatedBytes;
  const statusText =
    exportError ??
    source.error ??
    (source.loading ? '原本を読み込み中…' : (source.formatLabel ?? ''));
  const exportHint = ((): string => {
    if (source.error) return source.error;
    if (!source.ready) return '原本の読み込みが終わるまで待ってください';
    if (!supportsOsDragExport) {
      return 'ドラッグ書き出しは Chrome / Edge のみ対応。ダウンロードで書き出します';
    }
    if (!withinDragLimit) {
      return `ドラッグ書き出しは ${formatFileSize(DRAG_EXPORT_MAX_BYTES)} まで。選択が長いためダウンロードで書き出します`;
    }
    return '選択範囲を WAV でダウンロードします';
  })();

  return (
    <BarContainer $bottomOffset={bottomOffset} role="region" aria-label="オーディオインスペクタ">
      <BarPanel>
        {/* 上段: 再生コントロール + ファイル情報 + 書き出し */}
        <ControlRow>
          <div className="u-row u-gap8" style={{ flexShrink: 0 }}>
            <IconButton
              icon={transport.isPlaying ? 'pause' : 'play_arrow'}
              filled
              size={36}
              color="var(--soremo-gray)"
              // クリックイベントを引数として渡さない（外部 transport の toggle は
              // 任意引数を取るものがある）
              onClick={() => transport.toggle()}
              ariaLabel={transport.isPlaying ? '一時停止' : '再生'}
            />
            <ToolTip description={loopEnabled ? '範囲ループ ON' : '範囲ループ OFF'}>
              <IconButton
                icon="repeat"
                size={24}
                color={loopEnabled ? 'var(--soremo-blue)' : 'var(--soremo-light-gray)'}
                onClick={() => setLoopEnabled((prev) => !prev)}
                ariaLabel="範囲ループを切り替え"
              />
            </ToolTip>
          </div>

          <FileInfo className="u-col u-gap4">
            <ToolTip description={file.name}>
              <span className="heading-spacing-13 u-line-height-100 u-text-ellipsis u-w100">
                {file.name}
              </span>
            </ToolTip>
            <span className="bodytext-11 u-line-height-100 u-text-light-gray u-text-ellipsis">
              {statusText}
            </span>
          </FileInfo>

          <div className="u-row u-gap8" style={{ flexShrink: 0 }}>
            {committed &&
              (canDrag ? (
                <ToolTip description="Finder や DAW へドラッグして書き出し（クリックでダウンロード）">
                  <DragChip
                    $enabled
                    draggable
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onClick={() => void handleDownload()}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      void handleDownload();
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label="選択範囲を書き出し"
                  >
                    <span className="material-symbols-rounded">drag_pan</span>
                    <span className="bodytext-11">{formatFileSize(clipBytes)}</span>
                  </DragChip>
                </ToolTip>
              ) : preparing ? (
                <DragChip $enabled={false} aria-live="polite">
                  <span className="bodytext-11">切り出し中…</span>
                </DragChip>
              ) : (
                <ToolTip description={exportHint}>
                  <Button
                    size="small"
                    theme="secondary"
                    variant="outlined"
                    width="fit"
                    leftIcon="download"
                    onClick={() => void handleDownload()}
                    disabled={exporting || !source.ready}
                  >
                    {exporting ? '書き出し中…' : '選択範囲を書き出し'}
                  </Button>
                </ToolTip>
              ))}
            {onCreateBookmark && (
              <ToolTip
                description={
                  view
                    ? '選択範囲をブックマーク（B）'
                    : '現在位置をブックマーク（B）'
                }
              >
                <IconButton
                  icon="bookmark_add"
                  size={24}
                  color={view ? BOOKMARK_COLOR : 'var(--soremo-light-gray)'}
                  onClick={handleAddBookmark}
                  ariaLabel="ブックマークを追加"
                />
              </ToolTip>
            )}
            <IconButton icon="close" onClick={onClose} ariaLabel="インスペクタを閉じる" />
          </div>
        </ControlRow>

        {/* 下段: 波形 + 範囲選択レイヤー */}
        <WaveArea
          ref={selection.containerRef}
          onPointerDown={selection.onPointerDown}
          aria-label="波形（ドラッグで範囲選択）"
        >
          <WaveformCanvas
            peaks={source.peaks}
            currentTime={transport.currentTime}
            duration={timelineDuration}
            height={WAVEFORM_HEIGHT}
            channelMode="split"
          />
          {/* ブックマーク（選択レイヤー・再生ヘッドより下の重なり順） */}
          {timelineDuration > 0 &&
            bookmarks.map((bookmark) => {
              const startPct = ratio(bookmark.startSeconds);
              const isRange = bookmark.endSeconds != null;
              const widthPct = isRange
                ? Math.max(0, ratio(bookmark.endSeconds!) - startPct)
                : 0;
              return (
                <React.Fragment key={bookmark.id}>
                  {isRange ? (
                    <BookmarkRangeFill
                      style={{ left: `${startPct}%`, width: `${widthPct}%` }}
                    />
                  ) : (
                    <BookmarkPointLine style={{ left: `${startPct}%` }} />
                  )}
                  {/* 作成者アバターのピン（タイムライン編集のコメントピンと同様） */}
                  <BookmarkPin
                    type="button"
                    style={{ left: `${startPct}%` }}
                    // WaveArea の範囲選択ドラッグを始めさせない
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={() => handleBookmarkJump(bookmark)}
                    onMouseEnter={() => handleMarkerEnter(bookmark.id)}
                    onMouseLeave={handleMarkerLeave}
                    onFocus={() => handleMarkerEnter(bookmark.id)}
                    onBlur={handleMarkerLeave}
                    aria-label={
                      bookmark.label
                        ? `ブックマーク: ${bookmark.label}`
                        : 'ブックマーク'
                    }
                  >
                    <Avatar
                      size={BOOKMARK_PIN_SIZE}
                      src={bookmark.createdByAvatar ?? undefined}
                      name={bookmark.createdByName ?? undefined}
                    />
                  </BookmarkPin>
                </React.Fragment>
              );
            })}
          {view && (
            <>
              <SelectionRect
                style={{
                  left: `${ratio(view.start)}%`,
                  width: `${Math.max(0, ratio(view.end) - ratio(view.start))}%`,
                }}
              />
              <SelectionHandle
                style={{ left: `${ratio(view.start)}%` }}
                onPointerDown={selection.makeHandlePointerDown('start')}
                role="slider"
                aria-label="選択範囲の開始"
                aria-valuemin={0}
                aria-valuemax={timelineDuration}
                aria-valuenow={view.start}
              />
              <SelectionHandle
                style={{ left: `${ratio(view.end)}%` }}
                onPointerDown={selection.makeHandlePointerDown('end')}
                role="slider"
                aria-label="選択範囲の終了"
                aria-valuemin={0}
                aria-valuemax={timelineDuration}
                aria-valuenow={view.end}
              />
            </>
          )}
          <Playhead style={{ left: `${ratio(transport.currentTime)}%` }} />
          {/* ホバー / 編集中のブックマーク詳細（ラベルと編集・削除のみ。
              作成者はピンのアバターで分かるため出さない） */}
          {activeBookmark && (
            <BookmarkPopup
              // 端のマーカーでもパネル外へはみ出さないよう左位置をクランプする
              style={{
                left: `clamp(110px, ${ratio(activeBookmark.startSeconds)}%, calc(100% - 110px))`,
              }}
              onMouseEnter={cancelPopupClose}
              onMouseLeave={handleMarkerLeave}
              onPointerDown={(event) => event.stopPropagation()}
            >
              <div className="u-col u-gap4">
                {editingBookmark ? (
                  <BookmarkLabelInput
                    className="bodytext-13"
                    value={editingBookmark.value}
                    placeholder="ラベルを入力"
                    autoFocus
                    onChange={(event) =>
                      setEditingBookmark({
                        id: editingBookmark.id,
                        value: event.target.value,
                      })
                    }
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        commitBookmarkLabel();
                      } else if (event.key === 'Escape') {
                        event.preventDefault();
                        setEditingBookmark(null);
                      }
                    }}
                    onBlur={commitBookmarkLabel}
                  />
                ) : (
                  <span className="bodytext-13 u-text-ellipsis">
                    {activeBookmark.label || 'ブックマーク'}
                  </span>
                )}
                {(onUpdateBookmark || onDeleteBookmark) && !editingBookmark && (
                  <div className="u-row u-gap8">
                    {onUpdateBookmark && (
                      <IconButton
                        icon="edit"
                        size={18}
                        color="var(--soremo-light-gray)"
                        onClick={() =>
                          setEditingBookmark({
                            id: activeBookmark.id,
                            value: activeBookmark.label,
                          })
                        }
                        ariaLabel="ラベルを編集"
                      />
                    )}
                    {onDeleteBookmark && (
                      <IconButton
                        icon="delete"
                        size={18}
                        color="var(--soremo-light-gray)"
                        onClick={() => handleDeleteBookmark(activeBookmark.id)}
                        ariaLabel="ブックマークを削除"
                      />
                    )}
                  </div>
                )}
              </div>
            </BookmarkPopup>
          )}
        </WaveArea>

        {/* 最下段: 時刻表示（左: 再生位置 / 中央: 選択範囲 / 右: 総尺） */}
        <TimeRow>
          <span>{formatPreciseTime(transport.currentTime)}</span>
          <span style={{ flex: 1, textAlign: 'center' }}>
            {view
              ? `${formatPreciseTime(view.start)} – ${formatPreciseTime(view.end)}（${(view.end - view.start).toFixed(2)} 秒）`
              : 'ドラッグで範囲を選択'}
          </span>
          <span>{formatPreciseTime(timelineDuration)}</span>
        </TimeRow>
      </BarPanel>
    </BarContainer>
  );
};

export default AudioInspectorBar;
